import { builder } from '@builder.io/react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { audioSetFiltersChanged } from '../../../Audio/actions/AudioActions';
import {
  selectAuthPermissions,
  selectHasAnySubscription,
  selectUseSnapshotsUI,
  selectEnableSearchHistory,
} from '../../../auth/AuthSelectors';
import { searchOrigins } from '../../Navigation/constants';
import { SearchFilterContentTypes } from '../../Shared/enums';
import deferToNextFrame from '../../Shared/utils/deferToNextFrame';
import {
  isSearchAppLoaded,
  setLoadingForSearch,
  updateSearchOptionsAndRefresh,
} from '../actions/SearchActions';
import NavSearch from '../components/navSearch/NavSearch';
import { SearchBarVariant } from '../components/navSearch/NavSearchElements/searchUtils';
import SearchFilterOptions from '../entities/SearchFilterOptions';
import {
  selectIsEnterpriseMember,
  selectSearchFilterOptions,
} from '../selectors/searchSelectors';
import { fetchTypeahead } from '../utils/searchUtils';
import { SelectedSearchFilterOptions } from './MenuContainerInterfaces';

const constructSearchFilterOptions = (
  contentType,
  searchTerm,
  searchOrigin = searchOrigins.SEARCH_BAR
): Partial<SelectedSearchFilterOptions> => {
  const contentClass =
    SearchFilterOptions.getContentClassFromContentType(contentType);
  return {
    contentClass,
    contentType,
    isPagination: false,
    page: 1, // go back to page 1 when any filter is changed
    searchOrigin,
    searchTerm,
    similarTo: null,
    searchSimilarTitle: null,
  };
};

export const handleSubmit = (
  contentType: string,
  searchTerm: string,
  searchOrigin: string,
  dispatch: any,
  selectedSearchFilterOptions: SelectedSearchFilterOptions
) => {
  dispatch(setLoadingForSearch(true));

  // Defer work that might slow down UI updates.
  deferToNextFrame(() => {
    dispatch(audioSetFiltersChanged(true));
  });

  deferToNextFrame(() => {
    builder.init('e7fb3d1e4da14573bd2a1edb7bfee5f1');
    builder.track('search');
  });

  deferToNextFrame(() => {
    const newSelectedSearchFilterOptions = selectedSearchFilterOptions.update(
      constructSearchFilterOptions(contentType, searchTerm, searchOrigin)
    );
    dispatch(updateSearchOptionsAndRefresh(newSelectedSearchFilterOptions));
  });
};

function NavSearchContainer() {
  const dispatch = useDispatch();

  const selectedSearchFilterOptions = useSelector(selectSearchFilterOptions);
  const userHasAnySubscription = useSelector(selectHasAnySubscription);
  const isEnterpriseMember = useSelector(selectIsEnterpriseMember);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const useSnapshotsUI = useSelector(selectUseSnapshotsUI);
  const enableSearchHistory = useSelector(selectEnableSearchHistory);

  const { contentType = '' } = selectedSearchFilterOptions;

  return (
    <NavSearch
      contentType={contentType as SearchFilterContentTypes}
      onSubmit={(
        contentType: string,
        searchTerm: string,
        searchOrigin: string
      ) =>
        handleSubmit(
          contentType,
          searchTerm,
          searchOrigin,
          dispatch,
          selectedSearchFilterOptions
        )
      }
      userHasAnySubscription={userHasAnySubscription}
      fetchTypeahead={fetchTypeahead}
      isEnterpriseMember={isEnterpriseMember}
      pproPluginEnabled={pproPluginEnabled}
      isSearchAppLoaded={isSearchAppLoaded}
      useSnapshots={useSnapshotsUI}
      enableSearchHistory={enableSearchHistory}
      searchBarVariant={SearchBarVariant.TopNav}
    />
  );
}

export default NavSearchContainer;
